import {
  Text,
  Heading,
  Stack,
  SelectInput,
  FormControl,
  FormLabel,
  Box,
  Checkbox,
  Flex,
  Button,
  FormProvider,
  useForm,
  CheckboxGroup,
  useToast,
  TextInput,
  useWatch,
  useStoreSearch,
  SearchInput,
  Divider,
  Icon,
  Grid,
} from '@thirstycamel/ui'
import React, { memo } from 'react'
import type { UpdateUserDto } from '@thirsty-camel/hump-club/src/modules/user/dto/UpdateUserDto'
import type StoreEntity from '@ts/core/src/modules/store/store.entity'

import LayoutAccount from '../../layout/LayoutAccount'
import { useUser } from '../../hooks/useUser'
import Input from '../../components/Input'
import { useActions, useStore } from '../../store/hooks'
import { getNiceErrorMessage } from '../../utils/getNiceErrorMessage'
import StoreCardSlim from '../../components/StoreCardSlim'
import InputBottleshop from '../../components/InputBottleshop'

export const GENDERS = [
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
  { label: 'Other', value: 'OTHER' },
  {
    label: 'Prefer not to say',
    value: 'PREFER_NOT_TO_SAY',
  },
]

const FAVOURITE_DRINKS = [
  { label: 'Beer', value: 'likesBeer' },
  { label: 'Red Wine', value: 'likesRedWine' },
  { label: 'White Wine', value: 'likesWhiteWine' },
  { label: 'Sparkling Wine', value: 'likesSparklingWine' },
  { label: 'Cider', value: 'likesCider' },
  { label: 'Spirits', value: 'likesSpirits' },
  { label: 'Premix', value: 'likesPremix' },
]

interface UpdateUserFormValues extends UpdateUserDto {
  store: StoreEntity
}

const Account = () => {
  const form = useForm<UpdateUserFormValues>()
  const toast = useToast()
  const { user } = useUser()

  const { isFetching } = useStore(store => store.auth)
  const { updateUser } = useActions(actions => actions.auth)

  const search = useStoreSearch<StoreEntity>({ limit: 5 })

  const removeFavouriteStore = async id => {
    try {
      await updateUser({
        stores: user.stores.filter(s => s.id !== id),
      })
      toast({
        title: 'Bottleshop removed!',
        description: `We've removed the selected bottleshop from your favourites`,
        status: 'success',
        isClosable: true,
      })
    } catch (e) {
      toast({
        title: 'Error updating your profile',
        description: getNiceErrorMessage(e),
        status: 'error',
        isClosable: true,
      })
    }
  }

  const handleSubmit = form.handleSubmit(async values => {
    values.stores = undefined
    try {
      await updateUser(values)
      toast({
        title: 'Profile updated!',
        description: `We've updated your account`,
        status: 'success',
        isClosable: true,
      })
    } catch (e) {
      toast({
        title: 'Error updating your profile',
        description: getNiceErrorMessage(e),
        status: 'error',
        isClosable: true,
      })
    }
  })

  const handleAddBottleshop = async () => {
    const values = form.getValues()

    if (!values.store) {
      return
    }

    if (user.stores.find(s => s.id === values.store.id)) {
      toast({
        title: 'Bottleshop already a favourite',
        description: 'This bottleshop is already a favourite',
        status: 'error',
        isClosable: true,
      })

      return
    }

    try {
      await updateUser({
        stores: [
          ...user.stores,
          { id: values.store.id, name: values.store.name, address: values.store.address },
        ],
      })
      toast({
        title: 'Bottleshop added!',
        description: `We've added the selected bottleshop from your favourites`,
        status: 'success',
        isClosable: true,
      })

      form.setValue('store', null)
      form.setValue('store__search', '')
    } catch (e) {
      toast({
        title: 'Error updating your profile',
        description: getNiceErrorMessage(e),
        status: 'error',
        isClosable: true,
      })
    }
  }

  const changePassword = useWatch<string>({ control: form.control, name: 'changePassword' })
  const selectedStore = useWatch<StoreEntity>({ control: form.control, name: 'store' })

  return (
    <LayoutAccount title="My Account" requireAuth>
      {user?.hasCompletedOnboarding ? (
        <Heading fontSize="lg" mb={5}>
          EDIT ACCOUNT INFORMATION
        </Heading>
      ) : (
        <Stack mb={5}>
          <Heading fontSize="lg">WELCOME TO THE CLUB!</Heading>
          <Text color="blackAlpha.600">LET'S GET TO KNOW EACH OTHER A LITTLE MORE.</Text>
        </Stack>
      )}

      <FormProvider {...form}>
        <Stack as="form" spacing={5} onSubmit={handleSubmit} maxW="700px">
          {user?.hasCompletedOnboarding && (
            <Stack spacing={5}>
              <Flex direction={['column', 'row']}>
                <Input
                  as={TextInput}
                  type="text"
                  isRequired
                  isDisabled={isFetching}
                  name="firstName"
                  defaultValue={user?.firstName}
                  label="First Name"
                  mr={[, 5]}
                  mb={[5, 0]}
                />

                <Input
                  as={TextInput}
                  isRequired
                  isDisabled={isFetching}
                  type="text"
                  name="lastName"
                  defaultValue={user?.lastName}
                  label="Last Name"
                />
              </Flex>

              <Flex direction={['column', 'row']}>
                <Input
                  as={TextInput}
                  isReadOnly
                  type="text"
                  name="email"
                  isDisabled
                  defaultValue={user?.email}
                  label="Email"
                  mr={[, 5]}
                  mb={[5, 0]}
                />

                <Input
                  as={TextInput}
                  isReadOnly
                  type="text"
                  isDisabled
                  name="dateOfBirth"
                  defaultValue={user?.dateOfBirth.toString()}
                  label="Date of birth"
                />
              </Flex>

              <Box
                p={5}
                backgroundColor="gray.50"
                border="1px"
                borderColor="gray.200"
                borderRadius="sm"
              >
                <Flex alignItems="center">
                  <Input
                    as={TextInput}
                    isReadOnly
                    type="text"
                    name="mobilePhone"
                    label="Mobile"
                    defaultValue={user?.mobilePhone}
                    helperText="This is also your Hump Club number"
                    mr={5}
                  />

                  <Input ml={5} as={Checkbox} name="changePassword">
                    <Text fontFamily="heading">CHANGE PASSWORD</Text>
                  </Input>
                </Flex>

                {changePassword && (
                  <Stack mt={5}>
                    <Input
                      as={TextInput}
                      isDisabled={isFetching}
                      isRequired
                      type="password"
                      name="currentPassword"
                      label="Current password"
                    />

                    <Flex>
                      <Input
                        as={TextInput}
                        isRequired
                        isDisabled={isFetching}
                        type="password"
                        name="currentPassword"
                        label="New password"
                        mr={5}
                      />

                      <Input
                        as={TextInput}
                        isRequired
                        isDisabled={isFetching}
                        type="password"
                        name="currentPassword"
                        label="Confirm new password"
                      />
                    </Flex>
                  </Stack>
                )}
              </Box>
            </Stack>
          )}

          <Input
            as={SelectInput}
            name="gender"
            isDisabled={isFetching}
            options={GENDERS}
            defaultValue={user?.gender}
            placeholder="Select a gender..."
            label="Gender"
            maxW="25rem"
          />

          <FormLabel>Your favourite bottleshops</FormLabel>

          {user?.stores?.length ? (
            <Grid templateColumns="1fr 1fr" gridGap={2}>
              {user?.stores?.map(store => (
                <StoreCardSlim
                  showOpeningHours={false}
                  store={store}
                  showAddress
                  icon="delete"
                  onClickIcon={() => removeFavouriteStore(store.id)}
                />
              ))}
            </Grid>
          ) : (
            <Text>You currently have no favourite bottleshops</Text>
          )}

          <Stack maxW="25rem">
            <InputBottleshop handleSelectBottleshop={handleAddBottleshop} />
          </Stack>

          <Stack>
            <FormLabel>Your favourite drinks</FormLabel>
            <Flex wrap="wrap">
              {FAVOURITE_DRINKS.map(drink => (
                <Box>
                  <Input
                    as={Checkbox}
                    name={drink.value}
                    mb={5}
                    mr={5}
                    defaultIsChecked={user?.[drink.value]}
                  >
                    {drink.label}
                  </Input>
                </Box>
              ))}
            </Flex>
          </Stack>

          <Box display="inline-block">
            <Button type="submit" isLoading={isFetching} variantColor="pink">
              Save
            </Button>
          </Box>
        </Stack>
      </FormProvider>
    </LayoutAccount>
  )
}

export default memo(Account)
